import { defineStore } from 'pinia'
import service from '../utils/request'
import { buildBrowserSuccessUrl, clearBrowserLoginState, loadBrowserLoginState, pollBrowserSession, saveBrowserLoginState, safeReturnPath } from '../auth/browser-session.mjs'
import { createAuthLifecycle, logoutLocally } from '../auth/auth-lifecycle.mjs'

const lifecycle = createAuthLifecycle()
let profileRequest = null
let completionRequest = null

export const useAuthStore = defineStore('auth', {
  state: () => ({ userInfo: null, profileVerified: false, loginPending: false }),
  getters: { isAuthenticated: state => state.profileVerified && !!state.userInfo, getUserInfo: state => state.userInfo },
  actions: {
    initializeAuth() {},
    clearAuth() { lifecycle.invalidate(); this.userInfo = null; this.profileVerified = false },
    async beginBrowserLogin(returnPath = '/') {
      if (this.loginPending) return
      this.loginPending = true
      lifecycle.invalidate()
      const revision = lifecycle.snapshot()
      try {
      const profile = await service.get('/auth/me/').catch(error => {
        if (error.response?.status !== 401) throw error
        return null
      })
      if (!lifecycle.isCurrent(revision)) return
      if (profile) {
        this.userInfo = profile.data; this.profileVerified = true
        return safeReturnPath(returnPath)
      }
      this.userInfo = null; this.profileVerified = false
      const { data } = await service.post('/auth/browser-session/', { success_url: buildBrowserSuccessUrl(window.location) })
      if (!lifecycle.isCurrent(revision)) return
      saveBrowserLoginState(window.sessionStorage, data, returnPath)
      window.location.replace(data.login_url)
      } finally { this.loginPending = false }
    },
    completeBrowserLogin() {
      if (completionRequest && lifecycle.isCurrent(completionRequest.revision)) return completionRequest.promise
      lifecycle.invalidate()
      const revision = lifecycle.snapshot()
      const pending = { revision, promise: null }
      completionRequest = pending
      pending.promise = (async () => {
        const session = loadBrowserLoginState(window.sessionStorage)
        if (!session) {
          const user = await this.fetchUserInfo()
          return user && lifecycle.isCurrent(revision) ? '/' : undefined
        }
        let result
        try { result = await pollBrowserSession({ api: service, session }) }
        catch (error) {
          if (!lifecycle.isCurrent(revision)) return
          if (!error.sessionExpired) throw error
          const user = await this.fetchUserInfo()
          if (!user || !lifecycle.isCurrent(revision)) return
          result = { user }
        }
        if (!lifecycle.isCurrent(revision)) return
        lifecycle.invalidate(); this.userInfo = result.user; this.profileVerified = true
        // 保留事务返回路径；刷新完成页可读取后端缓存，不重复消费账号中心结果。
        return session.return_path
      })().finally(() => { if (completionRequest === pending) completionRequest = null })
      return pending.promise
    },
    fetchUserInfo() {
      if (profileRequest && lifecycle.isCurrent(profileRequest.revision)) return profileRequest.promise
      const pending = { revision: lifecycle.snapshot(), promise: null }
      profileRequest = pending
      const revision = pending.revision
      pending.promise = service.get('/auth/me/').then(({ data }) => {
        if (!lifecycle.isCurrent(revision)) return null
        this.userInfo = data; this.profileVerified = true
        return data
      }).catch(error => {
        if (!lifecycle.isCurrent(revision)) { error.authStale = true; throw error }
        if (lifecycle.isCurrent(revision)) {
          if ([401, 403].includes(error.response?.status)) this.clearAuth()
          else this.profileVerified = false
        }
        throw error
      })
        .finally(() => { if (profileRequest === pending) profileRequest = null })
      return pending.promise
    },
    async refreshIdentityOnReturn() {
      if (this.loginPending || (completionRequest && lifecycle.isCurrent(completionRequest.revision))) return
      this.profileVerified = false
      const previousEmail = this.userInfo?.email
      const revision = lifecycle.snapshot()
      try {
        const user = await this.fetchUserInfo()
        if (lifecycle.isCurrent(revision) && user && previousEmail && user.email !== previousEmail) window.location.reload()
      } catch (error) {
        if (error.response?.status !== 401) throw error
      }
    },
    async logout() {
      await logoutLocally({ lifecycle, request: () => service.post('/auth/logout/'), clear: () => { this.clearAuth(); clearBrowserLoginState(window.sessionStorage) }, redirect: () => { window.location.href = '/login' } })
    },
  },
})
